import {
  APIListValuesRequest,
  APIListValuesResponse,
  APIPaginatedRequest,
  CountRequest,
  DeleteRequest,
  getFetcher,
  getFetcherV2,
} from "shared/api/api";
import { clientV2 } from "shared/api/axios";
import { SmartGroup } from "shared/api/smartGroups/api";
import { createURL } from "shared/api/utils";

const INVESTIGATIONS_V2_BASE_ROUTE = "investigations";

const investigationURL = (...parts: string[]) =>
  createURL([INVESTIGATIONS_V2_BASE_ROUTE, ...parts]);

export interface Investigation {
  ID: string;
  createdAt: string;
  createdBy: string;
  updatedAt: string;
  updatedBy: string;
  archivedAt?: string | null;
  name: string;
  description?: string | null;
  assetSchemaName: string;
  eventSchemaName: string;
  assetFilter?: string | null;
  eventFilter?: string | null;
  externalID?: string | null;
  smartGroupID?: string | null;
  smartGroup?: SmartGroup | null;
}

export interface InvestigationPin {
  ID: string;
  investigationID: string;
  type: string;
  title: string;
  data: Record<string, unknown>;
  createdAt: string;
  updatedAt: string;
}

export interface ListInvestigationsRequest extends APIPaginatedRequest {
  skipRequest?: boolean;
}

export const listInvestigationsRequestURI = ({
  skipRequest,
  ...params
}: ListInvestigationsRequest): string | null =>
  skipRequest
    ? null
    : clientV2.getUri({
        method: "GET",
        url: investigationURL(),
        params,
      });

export const listInvestigationsCountRequestURI = (
  params: CountRequest
): string =>
  clientV2.getUri({
    method: "GET",
    url: investigationURL("count"),
    params,
  });

const listInvestigationsValuesRequestURI = ({
  fieldName,
  ...params
}: APIListValuesRequest): string =>
  clientV2.getUri({
    method: "get",
    url: investigationURL("values", fieldName),
    params,
  });

export const listInvestigationsValues = (args: APIListValuesRequest) =>
  getFetcher<APIListValuesResponse>(listInvestigationsValuesRequestURI(args));

export interface GetInvestigationRequest {
  id: string;
  skipRequest?: boolean;
}

export const getInvestigationRequestURI = ({
  id,
  skipRequest,
}: GetInvestigationRequest): string | null =>
  skipRequest || !id
    ? null
    : clientV2.getUri({
        method: "GET",
        url: investigationURL(id),
      });

export interface CreateInvestigationRequest {
  name: string;
  description?: string | null;
  assetSchemaName: string;
  eventSchemaName: string;
  assetFilter?: string | null;
  eventFilter?: string | null;
  externalID?: string | null;
  smartGroupID?: string | null;
}

export const createInvestigation = (data: CreateInvestigationRequest) =>
  clientV2.post<Investigation>(investigationURL(), data);

export interface UpdateInvestigationRequest extends Partial<CreateInvestigationRequest> {
  ID: string;
}

export const updateInvestigation = ({
  ID,
  ...data
}: UpdateInvestigationRequest) =>
  clientV2.patch<Investigation>(investigationURL(ID), data);

export const deleteInvestigation = ({ id }: DeleteRequest) =>
  clientV2.delete(investigationURL(id));

export interface ListInvestigationPinsRequest extends APIPaginatedRequest {
  id: string;
  skipRequest?: boolean;
}

export const listInvestigationPinsRequestURI = ({
  id,
  skipRequest,
  ...params
}: ListInvestigationPinsRequest): string | null =>
  skipRequest || !id
    ? null
    : clientV2.getUri({
        method: "GET",
        url: investigationURL(id, "pins"),
        params,
      });

export const listInvestigationPins = (args: ListInvestigationPinsRequest) =>
  getFetcherV2<InvestigationPin[]>(listInvestigationPinsRequestURI(args) ?? "");

export interface CreateInvestigationPinRequest {
  investigationID: string;
  type: string;
  title: string;
  data: Record<string, unknown>;
}

export const createInvestigationPin = ({
  investigationID,
  ...body
}: CreateInvestigationPinRequest) =>
  clientV2.post<InvestigationPin>(
    investigationURL(investigationID, "pins"),
    body
  );

export interface UpdateInvestigationPinRequest {
  investigationID: string;
  pinID: string;
  type?: string;
  title?: string;
  data?: Record<string, unknown>;
}

export const updateInvestigationPin = ({
  investigationID,
  pinID,
  ...body
}: UpdateInvestigationPinRequest) =>
  clientV2.patch<InvestigationPin>(
    investigationURL(investigationID, "pins", pinID),
    body
  );

export const deleteInvestigationPin = ({
  investigationID,
  pinID,
}: {
  investigationID: string;
  pinID: string;
}) => clientV2.delete(investigationURL(investigationID, "pins", pinID));
